import * as Sentry from '@sentry/react';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HashRouter, Routes } from 'react-router-dom';
import { has } from 'ramda';
import './App.scss';
import './styles/loading.scss';
import Header from './components/header/Header';
import Footer from './components/footer/Footer';
import ProgressBar from './features/progressBar/ProgressBar';
import { selectUser } from './features/user/userSlice';
import { cleanupDiscountedRailcards } from './features/railcards/railcardsSlice';
import { initGTM } from './utils/googleTagManager';
import { selectConfig } from './features/config/configSlice';
import type ConfigState from './interfaces/ConfigState';
import router from './app/router';
import Maintenance from './features/maintenance/Maintenance';
import Loader from './components/loader/Loader';
import { API_ACCESS_TOKENS, BASE_URLS, BRANDS } from './constants';
import './utils/i18n.js';

function App() {
  const user = useSelector(selectUser);

  const dispatch = useDispatch();

  const config = useSelector<ConfigState, ConfigState>(selectConfig);

  const { maintenanceMode } = config;

  useEffect(() => {
    const { hostname } = globalThis.location;

    const isDev = hostname.includes('localhost');
    const isSid = hostname.includes('otrldev.uk');
    const isStage = hostname.includes('stage.otrl.io');

    let baseURL = null;
    let brand = null;
    let environment = null;

    const { REACT_APP_ENVIRONMENT, REACT_APP_BASE_URL, REACT_APP_BRAND } = process.env;
    brand = REACT_APP_BRAND;

    if (isDev) {
      /*
         .env file should be created in root dir locally
         containing these values for local development
      */
      environment = REACT_APP_ENVIRONMENT;
      baseURL = REACT_APP_BASE_URL;
    } else if (isSid) {
      environment = 'sid';
      baseURL = `${hostname.split('.')[1]}.otrldev.uk`;
    } else if (isStage) {
      environment = 'stage';
      baseURL = 'stage.otrl.io';
    } else {
      // Assumed to be prod env
      environment = 'prod';
      baseURL = BASE_URLS[brand];
    }

    const apiAccessToken = API_ACCESS_TOKENS[environment];

    let config = {
      apiAccessToken,
      baseURL,
      brand,
      environment,
      isDev,
      isSid,
      enablePay360CardPayments: brand !== BRANDS.TP,
      enableBraintreeCardPayments: brand === BRANDS.TP,
      payPalEnabled: brand === BRANDS.SE || brand === BRANDS.TP || brand === BRANDS.TB,
      maintenanceMode: false,
      enableMarketingConsents: brand === BRANDS.TP,
      enableDiscountCode: brand === BRANDS.TB || brand === BRANDS.SN || brand === BRANDS.TL || brand === BRANDS.GN,
      enableUsercentrics: brand === BRANDS.TP || brand === BRANDS.TB,
    };

    // @ts-ignore ignore global window params
    const featureFlagOverrides = globalThis.featureFlagOverrides;

    if (featureFlagOverrides) {
      const {
        enablePay360CardPayments,
        enableBraintreeCardPayments,
        maintenanceMode,
        payPalEnabled,
        enableMarketingConsents,
        enableDiscountCode,
        enableUsercentrics,
      } = featureFlagOverrides;

      config = {
        ...config,
        ...(has('enablePay360CardPayments', featureFlagOverrides) && { enablePay360CardPayments }),
        ...(has('enableBraintreeCardPayments', featureFlagOverrides) && {
          enableBraintreeCardPayments,
        }),
        ...(has('maintenanceMode', featureFlagOverrides) && { maintenanceMode }),
        ...(has('payPalEnabled', featureFlagOverrides) && { payPalEnabled }),
        ...(has('enableMarketingConsents', featureFlagOverrides) && { enableMarketingConsents }),
        ...(has('enableDiscountCode', featureFlagOverrides) && { enableDiscountCode }),
        ...(has('enableUsercentrics', featureFlagOverrides) && { enableUsercentrics }),
      };
    }

    dispatch({ type: 'config/initialise', payload: { config } });
    dispatch({ type: 'paypal/getClientToken' });
    dispatch(cleanupDiscountedRailcards());
  }, [dispatch]);

  useEffect(() => {
    const { REACT_APP_BRAND } = process.env;
    const usesUC = REACT_APP_BRAND === BRANDS.TP || REACT_APP_BRAND === BRANDS.TB;
    initGTM(REACT_APP_BRAND, usesUC);
  }, []);

  const configReady = config && config.baseURL && config.brand && config.environment;

  useEffect(() => {
    if (!configReady) {
      return;
    }

    Sentry.init({
      dsn: 'https://05f4b00ce2f125936231745cc62a1c29@o31533.ingest.us.sentry.io/4510906438647808',
      release: process.env.REACT_APP_VERSION || 'unknown',
      environment: config.environment,
      beforeSend(event) {
        // Always keep CSP events
        const extra = event.extra as Record<string, unknown> | undefined;
        if (extra?.isCspViolation) {
          return event;
        }

        const message = event.exception?.values?.[0]?.value || event.message;

        const excludeErrors = [
          /TypeError: .*(null.*_currentElement|_currentElement.*null)/,
          /Error: Minified exception occurred/,
          /TypeError: Network request failed/,
          /TypeError: Failed to fetch/,
          /SyntaxError: Unexpected token </,
        ];

        if (!message) return null;
        if (excludeErrors.some((re) => re.test(message))) {
          return null;
        }

        return event;
      },
    });

    Sentry.setTags({
      app: 'digital-railcards-portal',
      brand: config.brand,
      environment: config.environment,
    });

    // CSP → Sentry bridge
    document.addEventListener('securitypolicyviolation', (e: SecurityPolicyViolationEvent) => {
      try {
        const directive = e.effectiveDirective || e.violatedDirective || 'csp';
        const blocked = e.blockedURI || '<inline>';

        Sentry.captureMessage(`CSP: ${directive} blocked ${blocked}`, {
          level: 'warning',
          fingerprint: ['csp', directive, blocked],
          extra: {
            isCspViolation: true,
            directive,
            blockedURI: e.blockedURI,
            documentURI: e.documentURI,
            originalPolicy: e.originalPolicy,
            referrer: e.referrer,
            sourceFile: e.sourceFile,
            lineNumber: e.lineNumber,
            columnNumber: e.columnNumber,
            disposition: e.disposition,
            violatedDirective: e.violatedDirective,
            effectiveDirective: e.effectiveDirective,
            statusCode: e.statusCode,
          },
        });
      } catch (err) {
        console.warn('Error capturing CSP violation:', err);
      }
    });
  }, [configReady, config.brand, config.environment]);

  return (
    <div className="digital-railcards-app">
      <Header user={user} />
      <div className="main-content">
        {configReady ? (
          maintenanceMode ? (
            <Maintenance />
          ) : (
            <HashRouter>
              <ProgressBar />
              <Routes>{router}</Routes>
            </HashRouter>
          )
        ) : (
          <Loader message="Loading railcards" unwrapped={false} inline={false} />
        )}
      </div>
      <Footer />
    </div>
  );
}

export default App;
